// src/pages/PrincipalDashboard.js (v9 – fully modernized with config & api)

import React, { useEffect, useRef, useState } from "react";
import "../styles/PrincipalDashboard.css";
import "../styles/PrincipalDashboardPanels.css";

import api from "../services/api";
import { API_PATHS } from "../config";

// Sections (self-contained components)
import ProfileCard from "../components/principal/ProfileCard";
import PhotosToReview from "../components/principal/PhotosToReview";
import StaffManager from "../components/principal/StaffManager";
import ParentInvite from "../components/principal/ParentInvite";
import WebsiteApiKey from "../components/principal/WebsiteApiKey";
import SubscriptionPanel from "../components/principal/SubscriptionPanel";
import YourPhotosGrid from "../components/principal/YourPhotosGrid";
import PrincipalSetupWizard from "../components/principal/PrincipalSetupWizard";

const STORAGE_KEY = "principal-dashboard.activeTab";

/**
 * Main dashboard for school principals.
 *
 * Features tabbed navigation, persistent tab state, keep-alive mounting,
 * onboarding wizard for new users, and real-time greeting.
 *
 * @component
 * @returns {JSX.Element}
 */
const PrincipalDashboard = () => {
  const [profile, setProfile] = useState({
    email: "",
    displayName: "",
    schoolName: "",
    totalPhotos: 0,
    instagramUsername: null,
    schoolHashtag: null,
    websiteTechUsed: null,
    schoolId: null,
    principalName: null,
    hasInstalledMobileApp: false,
  });

  const [loading, setLoading] = useState(true);
  const [instagramMessage, setInstagramMessage] = useState("");
  const [error, setError] = useState("");

  // Active tab — persist in localStorage
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved || "profile";
    } catch {
      return "profile";
    }
  });

  // Keep tabs mounted once opened (performance + state preservation)
  const [mountedTabs, setMountedTabs] = useState(new Set([activeTab]));

  const [hasPluginApiKey, setHasPluginApiKey] = useState(false);

  // Onboarding wizard
  const [showSetupWizard, setShowSetupWizard] = useState(false);
  const [setupInitialStep, setSetupInitialStep] = useState(1);

  const greeting = React.useMemo(() => {
    const h = new Date().getHours();
    if (h >= 5 && h < 12) return "Good morning";
    if (h >= 12 && h < 17) return "Good afternoon";
    return "Good evening";
  }, []);

  // Fetch profile once on mount
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    (async () => {
      try {
        const { data } = await api.get(API_PATHS.PROFILE);

        const emailPrefix = data.email?.split("@")[0] || "";
        setProfile({
          email: data.email || "",
          displayName: data.display_name || emailPrefix,
          schoolName: data.school_name || "N/A",
          totalPhotos: data.total_photos || 0,
          instagramUsername: data.instagram_username || "Not connected",
          schoolHashtag: data.school_hashtag || null,
          websiteTechUsed: data.website_tech_used || null,
          schoolId: data.school_id || "No school ID assigned",
          principalName: data.principal_name || "N/A",
          hasInstalledMobileApp: Boolean(data.has_installed_mobile_app),
        });
      } catch (e) {
        console.error("Failed to load principal profile:", e);
        setError("Failed to load your dashboard. Please refresh.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Handle return from Stripe/Instagram OAuth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get("success");
    const messageText = params.get("message");
    const errorText = params.get("error");
    const errorDesc = params.get("error_description");

    if (success === "true" && messageText) {
      setInstagramMessage(decodeURIComponent(messageText));
      setTimeout(() => {
        setInstagramMessage("");
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 5000);
    } else if (errorText && errorDesc) {
      setError(decodeURIComponent(errorDesc));
      setTimeout(() => {
        setError("");
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 4000);
    }
  }, []);

  // Determine if setup wizard should show
  useEffect(() => {
    if (loading) return;

    let wizardComplete = false;
    try {
      wizardComplete = localStorage.getItem("gg.principal.setupComplete") === "true";
    } catch (e) {
      // Ignore — fallback to "profile"
    }

    if (wizardComplete) {
      setShowSetupWizard(false);
      return;
    }

    const emailPrefix = profile.email?.split("@")[0] || "";
    const displayNameIsDefault = profile.displayName === emailPrefix;
    const needsDisplayName = displayNameIsDefault && profile.displayName;
    const needsHashtag = !profile.schoolHashtag;
    const needsMobileApp = !profile.hasInstalledMobileApp;

    let step = 1;
    if (!needsDisplayName && needsHashtag) step = 2;
    else if (!needsDisplayName && !needsHashtag) {
      step = needsMobileApp ? 3 : 4;
    }

    setSetupInitialStep(step);
    setShowSetupWizard(true);
  }, [loading, profile]);

  const handleSetupComplete = () => {
    setShowSetupWizard(false);
    try {
      localStorage.setItem("gg.principal.setupComplete", "true");
    } catch {
      // Ignore localStorage errors
    }
  };

  const isInstagramConnected =
    !!profile.instagramUsername && profile.instagramUsername !== "Not connected";

  // Tab management
  const handleTabChange = (nextId) => {
    setActiveTab(nextId);
    setMountedTabs((prev) => new Set(prev).add(nextId));
    try {
      localStorage.setItem(STORAGE_KEY, nextId);
    } catch {
      // Ignore localStorage errors
    }
  };

  const tabs = [
    { id: "profile", label: "Your Profile", render: () => <ProfileCard profile={profile} onProfileChange={setProfile} onError={setError} /> },
    { id: "photos-review", label: "Photos to Review", render: () => <PhotosToReview onError={setError} isInstagramConnected={isInstagramConnected} /> },
    { id: "staff", label: "Staff Management", render: () => <StaffManager onError={setError} /> },
    { id: "parents", label: "Parent Management", render: () => <ParentInvite onError={setError} /> },
    {
      id: "website",
      label: (
        <span className="tab-label-with-indicator">
          Your School’s Website
          {hasPluginApiKey && (
            <span className="tab-indicator-success" aria-label="API key ready" title="API key ready for WordPress plugin">
              ✅
            </span>
          )}
        </span>
      ),
      render: () => (
        <WebsiteApiKey
          active={activeTab === "website"}
          websiteTechUsed={profile.websiteTechUsed}
          schoolId={profile.schoolId}
          principalName={profile.principalName}
          onError={setError}
          onApiKeyReady={setHasPluginApiKey}
        />
      ),
    },
    { id: "subscription", label: "Subscription", render: () => <SubscriptionPanel /> },
    { id: "your-photos", label: "Your Uploaded Photos", render: () => <YourPhotosGrid active={activeTab === "your-photos"} onError={setError} /> },
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-card">
          <div className="loading-spinner" />
          <p>Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="principal-dashboard-container page">
      {showSetupWizard && (
        <PrincipalSetupWizard
          profile={profile}
          onProfileChange={setProfile}
          onError={setError}
          initialStep={setupInitialStep}
          visible={showSetupWizard}
          onComplete={handleSetupComplete}
        />
      )}

      <header className="principal-dashboard-header">
        <div className="principal-dashboard-heading">
          <p className="principal-dashboard-eyebrow">Principal dashboard</p>
          <h1 className="principal-dashboard-title">
            {greeting}, {profile.displayName}!
          </h1>
          <p className="principal-dashboard-subtitle">
            Manage staff, approve photos and connect your school website — all in one place.
          </p>
        </div>

        <div className="principal-dashboard-meta">
          {profile.schoolName && (
            <span className="principal-dashboard-school-chip">{profile.schoolName}</span>
          )}
          <div className="principal-dashboard-stat">
            <span className="principal-dashboard-stat-label">Photos shared</span>
            <span className="principal-dashboard-stat-value">{profile.totalPhotos}</span>
          </div>
        </div>
      </header>

      {instagramMessage && <p className="success-message" aria-live="polite">{instagramMessage}</p>}
      {error && <p className="error-message" aria-live="polite">{error}</p>}

      <section className="principal-dashboard-main">
        {/* Mobile tab selector */}
        <div className="principal-tabs-mobile">
          <label htmlFor="principal-tab-select" className="sr-only">Choose a section</label>
          <div className="principal-tabs-mobile-select-wrapper">
            <select
              id="principal-tab-select"
              value={activeTab}
              onChange={(e) => handleTabChange(e.target.value)}
            >
              {tabs.map((tab) => (
                <option key={tab.id} value={tab.id}>
                  {typeof tab.label === "string" ? tab.label : "Your School’s Website"}
                </option>
              ))}
            </select>
            <span className="principal-tabs-mobile-chevron" aria-hidden="true">▾</span>
          </div>
        </div>

        {/* Desktop tabs */}
        <div className="principal-tabs-nav" role="tablist" aria-label="Principal dashboard sections">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              className={`principal-tab-button ${activeTab === tab.id ? "active" : ""}`}
              aria-selected={activeTab === tab.id}
              onClick={() => handleTabChange(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab panels – keep-alive */}
        <div className="principal-tabs-content">
          {tabs.map((tab) => {
            if (!mountedTabs.has(tab.id)) return null;
            const isActive = tab.id === activeTab;
            return (
              <div
                key={tab.id}
                role="tabpanel"
                aria-hidden={!isActive}
                className={`principal-tab-panel ${isActive ? "active" : "hidden"}`}
              >
                {tab.render()}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default PrincipalDashboard;